import {useEffect} from 'react'
import {Banner} from '@primer/react'
import type {BannerProps} from '@primer/react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import type {BillingWarningBannerPayload} from '../../routes/workflows-layout-route'

export const BILLING_WARNING_BANNER_ID = 'billing-warning-banner'

type BillingWarningBanner = InitialBanner & BannerProps & {'data-test-selector'?: string}

function billingWarningBannerDescription({variant, ownerLogin}: BillingWarningBannerPayload) {
  switch (variant) {
    case 'legacy_plan':
      return (
        'GitHub Actions is not available for private repositories on legacy per-repository billing plans. ' +
        'Please visit your billing settings to upgrade your account.'
      )
    case 'access_not_included':
      return (
        <>
          The billing plan for <span className="text-bold">{ownerLogin}</span> does not include access to GitHub
          Actions. Please contact the organization owner or billing manager for questions about the current billing plan
        </>
      )
    case 'disabled':
      return "Your account's billing is currently locked. Please update your payment information."
    case 'other':
      // Preserves an existing Rails gap: trade-restricted/metered-lock reasons render a
      // heading-only banner with no body copy, so no case is added here.
      return undefined
  }
}

function billingWarningBannerPrimaryAction({variant, upgradePath}: BillingWarningBannerPayload) {
  if (variant !== 'legacy_plan' || !upgradePath) return undefined

  return (
    <Banner.PrimaryAction as="a" href={upgradePath}>
      Upgrade
    </Banner.PrimaryAction>
  )
}

function billingWarningBannerSecondaryAction({variant, learnMoreUrl}: BillingWarningBannerPayload) {
  if (variant !== 'legacy_plan' || !learnMoreUrl) return undefined

  return (
    <Banner.SecondaryAction as="a" href={learnMoreUrl}>
      Learn more about GitHub Actions
    </Banner.SecondaryAction>
  )
}

/** Builds the Actions billing-warning banner from its route payload. */
export function getBillingWarningBanner(data: BillingWarningBannerPayload): BillingWarningBanner {
  return {
    id: BILLING_WARNING_BANNER_ID,
    title: "GitHub Actions workflows can't be executed on this repository.",
    description: billingWarningBannerDescription(data),
    variant: 'warning',
    'data-test-selector': BILLING_WARNING_BANNER_ID,
    actionsLayout: 'stacked',
    primaryAction: billingWarningBannerPrimaryAction(data),
    secondaryAction: billingWarningBannerSecondaryAction(data),
    // Sourced from the layout-level route payload, which doesn't refetch on leaf-route soft navigation.
    persistOnNavigation: true,
  }
}

/** Keeps the billing-warning banner synchronized with the active workflows-layout route payload. */
export function useBillingWarningBanner({data}: {data?: BillingWarningBannerPayload | null}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    if (!data) {
      dismissBanner(BILLING_WARNING_BANNER_ID)
      return
    }

    const id = addBanner(getBillingWarningBanner(data))

    return () => {
      dismissBanner(id)
    }
  }, [data, addBanner, dismissBanner])
}
